import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { SpinnerRow } from '@workspace/ui/components/spinner-row'
import { Switch } from '@workspace/ui/components/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import {
  AlertCircle,
  ChevronDown,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  Plus,
  Trash2,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { DataRoomSectionKey, ShareLinkConfig, VisibilityConfig } from './types.js'

const ALL_SECTIONS: DataRoomSectionKey[] = ['fundraising', 'pitch', 'documents', 'team']

const SECTION_LABELS: Record<DataRoomSectionKey, string> = {
  fundraising: 'Fundraising',
  pitch: 'Pitch Recording',
  documents: 'Documents',
  team: 'Team',
}

const SECTION_HINTS: Record<DataRoomSectionKey, string> = {
  fundraising: 'Fundraising stage, target amount',
  pitch: 'AI Session recording',
  documents: 'Pitch deck, business plan, and other files',
  team: 'Team members and LinkedIn profiles',
}

interface ShareLinksDocument {
  /** Display name (label) for the document */
  name: string
  /** Human-readable file size (e.g. "2.4 MB") */
  size: string
  /** Backend document ID (for linked documents) */
  documentId?: string
  /**
   * Whether "Ask the profile" reads this file. False for a file with no text
   * transcript (an image, a spreadsheet, a PDF the parser failed on); the deck
   * is read as images and is always true.
   */
  inChatContext?: boolean
}

export interface ShareLinksProps {
  links: ShareLinkConfig[]
  documents: ShareLinksDocument[]
  onAddLink: (link: ShareLinkConfig) => void
  onUpdateLink: (id: string, updates: Partial<ShareLinkConfig>) => void
  onDeleteLink: (id: string) => void
  onLinkCopied?: (linkId: string) => void
  /** Arriving from the Share panel's "Share settings & analytics" — scroll this link into view and open it. */
  focusLinkId?: string
  /** The links query hasn't resolved yet — `links` is `[]` either way, so
   * without this the empty state flashes before the real list ever gets a
   * chance to render. */
  isLoading?: boolean
}

export function ShareLinks({
  links,
  documents,
  onAddLink,
  onUpdateLink,
  onDeleteLink,
  onLinkCopied,
  focusLinkId,
  isLoading,
}: ShareLinksProps) {
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const newNameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (creating) newNameInputRef.current?.focus()
  }, [creating])

  function handleCreate() {
    if (!newName.trim()) return
    onAddLink({
      id: crypto.randomUUID(),
      name: newName.trim(),
      slug: '',
      domain: `${window.location.origin}/s`,
      isDefault: false,
      isOutreach: false,
      enabled: true,
      includedInAnalytics: true,
      chatEnabled: true,
      requireEmailToView: false,
      allowDeckDownload: false,
      visibilityConfig: null,
      views: 0,
      investors: 0,
      avgTime: '—',
      createdAt: new Date().toISOString().slice(0, 10),
    })
    setNewName('')
    setCreating(false)
  }

  return (
    <div className="mt-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Share Links</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Create named links with custom section visibility for different investors
          </p>
        </div>
        {!creating && (
          <CtaButton size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
            Create link
          </CtaButton>
        )}
      </div>

      {/* Create form */}
      {creating && (
        <div className="rounded-xl border border-[#f97316]/30 bg-[#f97316]/[0.03] p-5">
          <p className="mb-3 text-xs font-medium text-foreground">New share link</p>
          <div className="flex items-center gap-3">
            <Input
              ref={newNameInputRef}
              placeholder="Link name (e.g. 'Link for Seedcamp')"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <CtaButton size="sm" onClick={handleCreate} disabled={!newName.trim()}>
              Create
            </CtaButton>
            <SecondaryButton
              size="sm"
              onClick={() => {
                setCreating(false)
                setNewName('')
              }}
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      )}

      {/* Link cards */}
      {links.map((link, index) => (
        <ShareLinkCard
          key={link.id}
          link={link}
          documents={documents}
          // The first link is the one everybody has, so it is the one that
          // teaches what these controls do — it opens expanded and the rest
          // stay folded away. A link arrived at via focusLinkId always opens.
          defaultExpanded={index === 0 || link.id === focusLinkId}
          focused={link.id === focusLinkId}
          onUpdate={(updates) => onUpdateLink(link.id, updates)}
          onDelete={() => onDeleteLink(link.id)}
          onCopied={() => onLinkCopied?.(link.id)}
        />
      ))}

      {isLoading && links.length === 0 && (
        <SpinnerRow className="py-12 text-sm">Loading your links…</SpinnerRow>
      )}

      {!isLoading && links.length === 0 && !creating && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border py-12">
          <Link2 className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No share links yet</p>
          <SecondaryButton size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
            Create your first link
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}

/** Helper: check if a section is visible (null config = all visible) */
function isSectionVisible(config: VisibilityConfig | null, key: DataRoomSectionKey): boolean {
  if (!config) return true
  return config.sections.includes(key)
}

function ShareLinkCard({
  link,
  documents,
  defaultExpanded = false,
  focused = false,
  onUpdate,
  onDelete,
  onCopied,
}: {
  link: ShareLinkConfig
  documents: ShareLinksDocument[]
  /** Start with Link Privacy Settings open, so the controls are discoverable. */
  defaultExpanded?: boolean
  /** Arrived at from elsewhere (the Share panel) — scroll into view once. */
  focused?: boolean
  onUpdate: (updates: Partial<ShareLinkConfig>) => void
  onDelete: () => void
  onCopied?: () => void
}) {
  const [copied, setCopied] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Native scrollIntoView walks every scrollable ancestor, including the
    // page's own <main> AND the outer document — the founder layout's <main>
    // is the only one that should move. Scroll it directly instead.
    if (!focused || !cardRef.current) return
    const scrollParent = cardRef.current.closest('main')
    if (!scrollParent) return
    const cardRect = cardRef.current.getBoundingClientRect()
    const parentRect = scrollParent.getBoundingClientRect()
    const top = cardRect.top - parentRect.top + scrollParent.scrollTop - 24
    scrollParent.scrollTo({ top, behavior: 'smooth' })
  }, [focused])

  function handleCopy() {
    const url = `${link.domain}/${link.slug}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    onCopied?.()
  }

  function toggleSection(key: DataRoomSectionKey) {
    const currentConfig = link.visibilityConfig
    const isCurrentlyOn = isSectionVisible(currentConfig, key)
    const allDocIds = documents.map((d) => d.documentId).filter(Boolean) as string[]

    if (isCurrentlyOn) {
      // Turning off: create/update config to remove this section
      const currentSections = currentConfig?.sections ?? [...ALL_SECTIONS]
      const newSections = currentSections.filter((s) => s !== key)
      onUpdate({
        visibilityConfig: {
          sections: newSections,
          documents: currentConfig?.documents ?? allDocIds,
        },
      })
    } else {
      // Turning on: add section back
      const currentSections = currentConfig?.sections ?? []
      onUpdate({
        visibilityConfig: {
          sections: [...currentSections, key],
          documents: currentConfig?.documents ?? allDocIds,
        },
      })
    }
  }

  function toggleDocument(docId: string) {
    const currentConfig = link.visibilityConfig
    const allDocIds = documents.map((d) => d.documentId).filter(Boolean) as string[]
    const currentDocs = currentConfig?.documents ?? allDocIds

    const isSelected = currentDocs.includes(docId)
    const newDocs = isSelected ? currentDocs.filter((id) => id !== docId) : [...currentDocs, docId]

    onUpdate({
      visibilityConfig: {
        sections: currentConfig?.sections ?? [...ALL_SECTIONS],
        documents: newDocs,
      },
    })
  }

  const allDocIds = documents.map((d) => d.documentId).filter(Boolean) as string[]
  const visibleDocIds = link.visibilityConfig?.documents ?? allDocIds

  return (
    <div
      ref={cardRef}
      className={cn(
        'scroll-mt-24 rounded-xl border border-border bg-card/30',
        !link.enabled && 'opacity-50',
        focused && 'ring-2 ring-primary/60'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-emerald-500/10">
            <Link2 className="size-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{link.name}</p>
            <p className="text-[10px] text-muted-foreground">Created {link.createdAt}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3 pl-4">
          <button
            type="button"
            onClick={onDelete}
            disabled={link.isDefault || link.isOutreach}
            className={cn(
              'flex size-7 items-center justify-center rounded-md transition-colors',
              link.isDefault || link.isOutreach
                ? 'cursor-not-allowed text-muted-foreground/30'
                : 'text-muted-foreground hover:bg-red-500/10 hover:text-red-400'
            )}
          >
            <Trash2 className="size-3.5" />
          </button>
          <Switch
            checked={link.enabled}
            onCheckedChange={(checked) => onUpdate({ enabled: checked })}
          />
        </div>
      </div>

      {/* URL + stats */}
      <div className="flex flex-col gap-2 border-b border-border px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {link.domain}/<span className="font-medium text-foreground">{link.slug}</span>
          </span>
          <CtaButton size="sm" onClick={handleCopy} className="shrink-0">
            {copied ? 'Copied' : 'Copy link'}
          </CtaButton>
          {/* Named, not an icon: seeing the page as an investor sees it is the
              whole question this page answers, and it was invisible as a
              7-pixel glyph. */}
          <a
            href={`${link.domain}/${link.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className={secondaryLinkClasses({ size: 'sm' })}
          >
            <ExternalLink />
            <span>Preview</span>
          </a>
        </div>
        <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="size-3" />
            {link.views} views
          </span>
          <span className="flex items-center gap-1">
            <Users className="size-3" />
            {link.investors} investors
          </span>
          <span className="flex items-center gap-1">
            <Clock className="size-3" />
            {link.avgTime}
          </span>
        </div>
      </div>

      {/* Link privacy settings — collapsed by default. Both the set-once
          privacy toggles AND section visibility live under one trigger; the
          URL/copy/preview row above is what founders actually revisit. */}
      <Collapsible
        defaultOpen={defaultExpanded}
        className="transition-colors hover:bg-white/[0.04]"
      >
        <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-5 py-4">
          <p className="text-xs font-medium text-muted-foreground">Link Privacy Settings</p>
          <ChevronDown className="size-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/* Include in analytics toggle */}
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Include in analytics</span>
              <span className="text-[10px] text-muted-foreground">
                When off, views and sessions from this link are excluded from Investor's Feedback
                and AI reports.
              </span>
            </div>
            <Switch
              checked={link.includedInAnalytics}
              onCheckedChange={(checked) => onUpdate({ includedInAnalytics: checked })}
            />
          </div>

          {/* Require email to view */}
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Require email to view</span>
              <span className="text-[10px] text-muted-foreground">
                Adds friction: some investors will not enter an email. Best for links you post
                publicly.
              </span>
            </div>
            <Switch
              checked={link.requireEmailToView}
              onCheckedChange={(checked) => onUpdate({ requireEmailToView: checked })}
            />
          </div>

          {/* Allow deck download */}
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Allow deck download</span>
              <span className="text-[10px] text-muted-foreground">
                Off by default. When on, investors can save the deck PDF; many keep decks in their
                CRM and reread offline.
              </span>
            </div>
            <Switch
              checked={link.allowDeckDownload}
              onCheckedChange={(checked) => onUpdate({ allowDeckDownload: checked })}
            />
          </div>

          {/* Section toggles */}
          <div className="border-t border-border px-5 py-4">
            <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Visible sections
            </p>
            <div className="space-y-1">
              {ALL_SECTIONS.map((key) => {
                const isOn = isSectionVisible(link.visibilityConfig, key)

                return (
                  <div key={key}>
                    <div className="-mx-2 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-white/[0.02]">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-foreground">{SECTION_LABELS[key]}</span>
                        {SECTION_HINTS[key] && (
                          <span className="text-[10px] text-muted-foreground/50">
                            {SECTION_HINTS[key]}
                          </span>
                        )}
                        {key === 'documents' && isOn && visibleDocIds.length < allDocIds.length && (
                          <span className="rounded bg-[#f97316]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#f97316]">
                            {visibleDocIds.length} of {allDocIds.length}
                          </span>
                        )}
                      </div>
                      <Switch checked={isOn} onCheckedChange={() => toggleSection(key)} />
                    </div>

                    {/* Document granular selection */}
                    {key === 'documents' && isOn && documents.length > 0 && (
                      <GranularItemList
                        items={documents.map((d) => ({
                          id: d.documentId ?? d.name,
                          name: d.name,
                          detail: d.size,
                          icon: <FileText className="size-3 text-muted-foreground" />,
                          tag:
                            link.chatEnabled && d.inChatContext === false
                              ? {
                                  label: 'Excluded from AI context',
                                  detail: 'We currently do not support this type of document.',
                                }
                              : undefined,
                        }))}
                        selected={visibleDocIds}
                        onToggle={toggleDocument}
                      />
                    )}

                    {/* The founder's one decision about what investors can do on
                        the Profile. Not a section key: it has its own column. */}
                    {key === 'fundraising' && (
                      <div className="-mx-2 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-white/[0.02]">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-foreground">
                            Show the chat on your profile
                          </span>
                          <span className="text-[10px] text-muted-foreground/50">
                            Answered from your deck and record. You never see what investors ask.
                          </span>
                        </div>
                        <Switch
                          checked={link.chatEnabled}
                          onCheckedChange={(checked) => onUpdate({ chatEnabled: checked })}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

/** Indented list of switches for granular item selection */
function GranularItemList({
  items,
  selected,
  onToggle,
}: {
  items: {
    id: string
    name: string
    detail?: string
    icon?: React.ReactNode
    /** A caveat shown as a small tag beside the name, explained on hover. */
    tag?: { label: string; detail: string }
  }[]
  selected: string[]
  onToggle: (id: string) => void
}) {
  return (
    <div className="space-y-0.5 pb-0.5">
      {items.map((item) => {
        const isChecked = selected.includes(item.id)
        return (
          <button
            type="button"
            key={item.id}
            onClick={() => onToggle(item.id)}
            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white/[0.02]"
          >
            <div className="flex items-center gap-2 pl-4">
              {item.icon}
              <span
                className={cn('text-xs', isChecked ? 'text-foreground' : 'text-muted-foreground')}
              >
                {item.name}
              </span>
              {item.detail && (
                <span className="text-[10px] text-muted-foreground">{item.detail}</span>
              )}
              {item.tag && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded border border-amber-500/40 px-1.5 py-px text-[10px] text-amber-500">
                      <AlertCircle className="size-2.5" aria-hidden />
                      {item.tag.label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-64">
                    {item.tag.detail}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <Switch checked={isChecked} />
          </button>
        )
      })}
    </div>
  )
}
